/**
 * Timestamped video breakdown table with clickable rows
 */

import { timestampToSeconds } from '@/lib/timeUtils';
import type { TimelineEntry, BrandVisibility } from '@/types';

export interface TimelineTableProps {
  timeline: TimelineEntry[];
  currentTime: number;
  onRowClick: (timestamp: string) => void;
}

function getBrandVisibilityBadge(visibility: BrandVisibility) {
  const styles: Record<BrandVisibility, string> = {
    none: 'bg-gray-100 text-gray-600',
    visible: 'bg-green-100 text-green-700',
    partial: 'bg-yellow-100 text-yellow-700',
    clear: 'bg-green-100 text-green-700',
  };

  const labels: Record<BrandVisibility, string> = {
    none: 'None',
    visible: 'Visible',
    partial: 'Partial',
    clear: 'Clear',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${styles[visibility]}`}>
      {labels[visibility]}
    </span>
  );
}

export function TimelineTable({ timeline, currentTime, onRowClick }: TimelineTableProps) {
  // Determine which row is currently active based on video time
  const activeRowIndex = timeline.findIndex((entry, index) => {
    const startTime = timestampToSeconds(entry.timeStart);
    const endTime = timestampToSeconds(entry.timeEnd);
    const nextEntry = timeline[index + 1];
    const nextStart = nextEntry
      ? timestampToSeconds(nextEntry.timeStart)
      : Infinity;

    return currentTime >= startTime && currentTime < Math.min(endTime, nextStart);
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Time
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Scene
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Text
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Audio
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Brand
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Product
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {timeline.map((entry, index) => {
            const isActive = index === activeRowIndex;

            return (
              <tr
                key={index}
                onClick={() => onRowClick(entry.timeStart)}
                className={`cursor-pointer transition-colors ${
                  isActive
                    ? 'bg-blue-50 border-l-4 border-blue-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                {/* Time */}
                <td className="px-4 py-3 text-sm font-mono font-semibold text-gray-900 whitespace-nowrap">
                  {entry.timeStart} - {entry.timeEnd}
                </td>

                {/* Scene */}
                <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                  {entry.scene}
                </td>

                {/* Text */}
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                  {entry.text || (
                    <span className="text-gray-400 italic">No text</span>
                  )}
                </td>

                {/* Audio */}
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                  {entry.audio || (
                    <span className="text-gray-400 italic">No audio</span>
                  )}
                </td>

                {/* Brand Visibility */}
                <td className="px-4 py-3">
                  {getBrandVisibilityBadge(entry.brandVisibility)}
                </td>

                {/* Product Visibility */}
                <td className="px-4 py-3">
                  {getBrandVisibilityBadge(entry.productVisibility)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {timeline.length === 0 && (
        <div className="py-12 text-center text-gray-500">
          <p>No timeline data available</p>
        </div>
      )}
    </div>
  );
}
